import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { normalizePaymentMethodTypeKey } from '../utils/paymentMethodSlug';

/** Clave interna libre (p. ej. `nequi_tienda`); la UI visible es `method_name`. */
export type PaymentMethodType = string;

export interface PaymentMethod {
  id: string;
  empresa_id: string;
  method_type: PaymentMethodType;
  method_name: string;
  description?: string;
  account_info: Record<string, any>;
  icon?: string;
  is_active: boolean;
  display_order: number;
  show_on_invoices: boolean;
  show_on_quotes: boolean;
  available_online?: boolean; // Whether this method is available for online orders
  created_at: string;
  updated_at: string;
}

interface PaymentMethodsStore {
  paymentMethods: PaymentMethod[];
  isLoading: boolean;
  error: string | null;

  fetchPaymentMethods: (empresaId: string) => Promise<void>;
  createPaymentMethod: (empresaId: string, data: Partial<PaymentMethod>) => Promise<PaymentMethod | null>;
  updatePaymentMethod: (id: string, data: Partial<PaymentMethod>) => Promise<void>;
  deletePaymentMethod: (id: string) => Promise<void>;
  getActivePaymentMethods: (empresaId: string) => PaymentMethod[];
}

export const usePaymentMethodsStore = create<PaymentMethodsStore>((set, get) => ({
  paymentMethods: [],
  isLoading: false,
  error: null,

  fetchPaymentMethods: async (empresaId: string) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('empresa_id', empresaId)
        .eq('is_active', true) // Only fetch active payment methods
        .order('display_order', { ascending: true });

      if (error) {
        // Log detailed error for Safari debugging
        console.error('❌ Error fetching payment methods:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          status: (error as any).status || 'unknown',
          empresaId
        });
        throw error;
      }
      
      const methods = (data as PaymentMethod[]) || [];
      console.log('✅ Payment methods fetched:', methods.length, 'for empresa:', empresaId);
      set({ paymentMethods: methods, isLoading: false });
    } catch (err: any) {
      console.error('❌ Error in fetchPaymentMethods:', err);
      set({ error: err.message || 'Error loading payment methods', isLoading: false });
    }
  },

  createPaymentMethod: async (empresaId: string, data: Partial<PaymentMethod>) => {
    try {
      // All fields are optional - use defaults only if not provided
      const typeKey = normalizePaymentMethodTypeKey(
        String(data.method_type || ''),
        String(data.method_name || 'metodo')
      );
      const paymentData = {
        empresa_id: empresaId,
        method_type: typeKey,
        method_name: data.method_name || '', // Empty string is allowed
        description: data.description || null,
        account_info: data.account_info || {},
        icon: data.icon || null,
        is_active: data.is_active ?? true,
        show_on_invoices: data.show_on_invoices ?? true,
        show_on_quotes: data.show_on_quotes ?? true,
        available_online:
          data.available_online ??
          !['efectivo', 'tarjeta'].includes(typeKey),
        display_order: data.display_order ?? (get().paymentMethods?.length || 0),
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 Creating payment method:', { method_type: paymentData.method_type, empresa_id: paymentData.empresa_id });
      }

      const { data: newMethod, error } = await supabase
        .from('payment_methods')
        .insert(paymentData)
        .select()
        .single();

      if (error) {
        console.error('❌ Error creating payment method:', error);
        throw error;
      }
      if (!newMethod) return null;

      set((state) => ({ paymentMethods: [...state.paymentMethods, newMethod as PaymentMethod] }));
      return newMethod as PaymentMethod;
    } catch (err: any) {
      console.error('❌ Error creating payment method:', err);
      set({ error: err.message || 'Error creating payment method' });
      throw err;
    }
  },

  updatePaymentMethod: async (id: string, data: Partial<PaymentMethod>) => {
    const prev = get().paymentMethods.find((p) => p.id === id);
    const patch: Partial<PaymentMethod> = { ...data };
    if (data.method_type != null || data.method_name != null) {
      patch.method_type = normalizePaymentMethodTypeKey(
        String(data.method_type ?? prev?.method_type ?? ''),
        String(data.method_name ?? prev?.method_name ?? 'metodo')
      );
    }
    const { error } = await supabase.from('payment_methods').update(patch).eq('id', id);
    if (error) {
      console.error('Error updating payment method:', error);
      throw new Error(error.message || 'Error al actualizar el método de pago');
    }
    set((state) => ({
      paymentMethods: state.paymentMethods.map((pm) => (pm.id === id ? { ...pm, ...patch } : pm)),
    }));
  },

  deletePaymentMethod: async (id: string) => {
    const { error } = await supabase.from('payment_methods').delete().eq('id', id);
    if (error) {
      console.error('Error deleting payment method:', error);
      throw new Error(error.message || 'Error al eliminar el método de pago');
    }
    set((state) => ({ paymentMethods: state.paymentMethods.filter((pm) => pm.id !== id) }));
  },

  getActivePaymentMethods: (empresaId: string) => {
    return get()
      .paymentMethods.filter((pm) => pm.empresa_id === empresaId && pm.is_active)
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  },
}));



